import React, { useState, useEffect } from 'react';
import TimeInput from '../components/TimeInput';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { 
  ChevronLeft, 
  ChevronRight, 
  Plus,
  MapPin,
  Clock,
  Image,
  FileText,
  Timer,
  Edit,
  Trash2,
  Loader2,
  Save,
  X,
  GripVertical,
  Upload,
  Archive,
  Search,
  MoreHorizontal,
  User,
  Calendar,
  RotateCcw
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays, getWeek, getYear, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { getStatusColor, getStatusStyle, getPriorityColor, getContrastTextColor } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '../components/ui/hover-card';
import MapButtons from '../components/MapButtons';
import V2MobileCalendar from '../components/V2MobileCalendar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format a "HH:mm" 24h time string as "h:mm AM/PM" (US 12h). Returns input as-is on parse failure.
const formatTime12h = (t) => {
  if (!t || typeof t !== 'string') return t || '';
  const m = t.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return t;
  let h = parseInt(m[1], 10);
  const min = m[2];
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${period}`;
};

// Drag and drop state management
let draggedJob = null;
let draggedEvent = null;

const emptyJobForm = {
  client_name: '',
  phone: '',
  email: '',
  service_address: '',
  service_street: '',
  service_city: '',
  service_state: '',
  service_zip: '',
  job_title: '',
  service_type: '',
  date: '',
  time: '',
  duration: '',
  priority: 'Normal',
  status: 'Scheduled',
  notes: '',
  assigned_team_members: []
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, hasPermission, user, updatePreference } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [team, setTeam] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  // Calendar view mode — persisted to localStorage so the user's selection
  // sticks across page navigation and reloads. New default is 3 Weeks (the
  // view the office team uses most frequently). Falls back gracefully when
  // localStorage is unavailable (private mode etc).
  const [viewMode, setViewModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('cal.viewMode');
      const allowed = ['month','week','day','3day','6day','3week','3month','custom'];
      return saved && allowed.includes(saved) ? saved : '3week';
    } catch { return '3week'; }
  });
  const setViewMode = (v) => {
    setViewModeState(v);
    try { localStorage.setItem('cal.viewMode', v); } catch { /* ignore */ }
  };
  const [loading, setLoading] = useState(true);
  const [calendarSearch, setCalendarSearch] = useState('');
  
  // Legacy/Imported events state
  const [showLegacy, setShowLegacy] = useState(false);
  const [importedEvents, setImportedEvents] = useState([]);
  const [loadingImported, setLoadingImported] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);
  const [selectedLegacyEvent, setSelectedLegacyEvent] = useState(null);
  const [showLegacyDetailDialog, setShowLegacyDetailDialog] = useState(false);

  // Calendar Events (PTO, Holidays, Training, etc.)
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const emptyEventForm = {
    title: '',
    type: 'PTO',
    date: format(new Date(), 'yyyy-MM-dd'),
    end_date: '',
    all_day: true,
    start_time: '',
    end_time: '',
    color: '',
    assigned_to: [],
    location: '',
    notes: '',
  };
  const [eventForm, setEventForm] = useState(emptyEventForm);
  const [savingEvent, setSavingEvent] = useState(false);
  const [draggingEvent, setDraggingEvent] = useState(null);
  
  // Modal states
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMobileActionDialog, setShowMobileActionDialog] = useState(false);
  // Mobile-only Day Sheet — tapping a day cell or chip on mobile opens this list of all that day's items
  const [showDaySheet, setShowDaySheet] = useState(false);
  const [daySheetDate, setDaySheetDate] = useState(null);
  // V2 day-sheet: when calendar_layout === 'v2', the jobs section collapses
  // into a single "X Jobs Scheduled" roll-up. This tracks whether the user
  // tapped the roll-up to expand the full job list inline. Always reset on
  // sheet open so it lands collapsed by default.
  const [v2JobsExpanded, setV2JobsExpanded] = useState(false);

  // Per-user calendar layout preference. Default is V1 (current). Users
  // opt into V2 (mobile day-sheet jobs roll-up) from Profile → Display.
  // Stored on user.preferences so it persists across devices.
  const calendarLayout = (user?.preferences?.calendar_layout) === 'v2' ? 'v2' : 'v1';
  const isV2Calendar = calendarLayout === 'v2';
  // V2 mobile: hide the toolbar (search, view-mode toggles, # off, etc.)
  // by default and let the user expand it on demand. The mini-month is
  // already a reference card, so dumping the full toolbar above it is noise
  // for the field crew.
  const [v2HeaderExpanded, setV2HeaderExpanded] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobForm, setJobForm] = useState(emptyJobForm);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Drag and drop states
  const [draggingJob, setDraggingJob] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  
  // Calendar display mode - compact shows fewer jobs, expanded shows more
  const [calendarDisplayMode, setCalendarDisplayMode] = useState('expanded'); // 'compact' or 'expanded'

  // Per-view-mode resizable cell height. Office users wanted the 3/6 day and
  // week views to be deeper than the default. Stored in localStorage keyed by
  // `${viewMode}.${calendarDisplayMode}` so each view remembers its own height.
  const DEFAULT_CELL_HEIGHTS = { expanded: 180, compact: 120 };
  // Only these views are user-resizable (month/3month grids would push too
  // many rows off-screen; day view has its own dedicated layout).
  const RESIZABLE_VIEWS = ['3day', '6day', 'week', '3week', 'custom'];
  const cellHeightKey = `cal.cellHeight.${viewMode}.${calendarDisplayMode}`;
  const [cellHeight, setCellHeight] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(`cal.cellHeight.month.expanded`), 10);
      return Number.isFinite(v) ? v : DEFAULT_CELL_HEIGHTS.expanded;
    } catch { return DEFAULT_CELL_HEIGHTS.expanded; }
  });
  // Reload cellHeight whenever the view mode or display mode changes so each
  // (viewMode × displayMode) pair has its own remembered height.
  useEffect(() => {
    try {
      const v = parseInt(localStorage.getItem(cellHeightKey), 10);
      setCellHeight(Number.isFinite(v) ? v : DEFAULT_CELL_HEIGHTS[calendarDisplayMode]);
    } catch {
      setCellHeight(DEFAULT_CELL_HEIGHTS[calendarDisplayMode]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, calendarDisplayMode]);
  const persistCellHeight = (h) => {
    setCellHeight(h);
    try { localStorage.setItem(cellHeightKey, String(h)); } catch { /* ignore */ }
  };
  const resetCellHeight = () => {
    const def = DEFAULT_CELL_HEIGHTS[calendarDisplayMode];
    setCellHeight(def);
    try { localStorage.removeItem(cellHeightKey); } catch { /* ignore */ }
  };
  const isCellHeightResizable = RESIZABLE_VIEWS.includes(viewMode);
  const isCellHeightOverridden =
    isCellHeightResizable && cellHeight !== DEFAULT_CELL_HEIGHTS[calendarDisplayMode];

  const showJobNumbers = user?.preferences?.showJobNumbers_calendar !== false;
  const toggleJobNumbers = () => updatePreference('showJobNumbers_calendar', !showJobNumbers);

  const [customRangeStart, setCustomRangeStart] = useState('');
  const [customRangeEnd, setCustomRangeEnd] = useState('');
  const [editingOrder, setEditingOrder] = useState(null); // {jobId, date, value}
  
  const handleSetDailyOrder = async (jobId, date, order) => {
    try {
      await axios.put(`${API}/jobs/${jobId}/daily-order`, { date, order: order || null });
      setJobs(prev => prev.map(j => {
        if (j.id !== jobId) return j;
        const dailyOrder = { ...(j.daily_order || {}) };
        if (order) dailyOrder[date] = parseInt(order);
        else delete dailyOrder[date];
        return { ...j, daily_order: dailyOrder };
      }));
      setEditingOrder(null);
    } catch {
      toast.error('Failed to set order');
    }
  };
  
  // Check if device is mobile/touch
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Permission checks
  const canCreateJobs = isAdmin() || hasPermission('create_jobs');
  const canEditAllJobs = isAdmin() || hasPermission('edit_jobs');
  const canDeleteJobs = isAdmin() || hasPermission('delete_jobs');
  
  // Helper: Check if user can edit a specific job
  // Admins can edit all, technicians can only edit jobs they're assigned to
  const canEditThisJob = (job) => {
    if (!job) return false;
    if (canEditAllJobs) return true;
    // Check if current user is assigned to this job
    const assignedIds = job.assigned_team_members || job.team_member_ids || [];
    return assignedIds.includes(user?.id);
  };

  useEffect(() => {
    fetchJobs();
    fetchReferenceData();
    fetchImportStats();
    fetchEvents();
    fetchEventTypes();
    
    // Check URL params for showLegacy and date
    const showLegacyParam = searchParams.get('showLegacy');
    const dateParam = searchParams.get('date');
    
    if (showLegacyParam === 'true') {
      setShowLegacy(true);
    }
    
    if (dateParam) {
      try {
        setSelectedDate(parseISO(dateParam));
      } catch (e) {
        console.error('Invalid date param:', dateParam);
      }
    }
  }, [searchParams]);

  // Fetch imported events when showLegacy is toggled or month changes
  useEffect(() => {
    if (showLegacy) {
      fetchImportedEvents();
    }
  }, [showLegacy, selectedDate]);

  const fetchImportedEvents = async () => {
    setLoadingImported(true);
    try {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      const response = await axios.get(`${API}/imported-events/calendar/${year}/${month}`);
      setImportedEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch imported events:', error);
    } finally {
      setLoadingImported(false);
    }
  };

  const fetchImportStats = async () => {
    try {
      const response = await axios.get(`${API}/imported-events/stats`);
      setImportStats(response.data);
    } catch (error) {
      // Silent fail - stats are optional
    }
  };

  const fetchJobs = async () => {
    try {
      const fields = 'id,job_number,job_title,client_name,contact_name,phone,email,service_address,service_street,service_city,service_state,service_zip,service_type,date,end_date,time,duration,priority,status,notes,assigned_team_members,team_member_ids,assigned_equipment_ids,assigned_equipment_snapshot,job_days,is_multi_day,daily_series_id,daily_series_position,daily_series_total,daily_order,archived,files,photos';
      const response = await axios.get(`${API}/jobs?include_archived=true&fields=${fields}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/events/types`);
      setEventTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const getEventColor = (type) => {
    const t = eventTypes.find(t => t.name === type);
    return t?.color || '#f1f5f9';
  };

  const getEventTextColor = (type) => {
    const t = eventTypes.find(t => t.name === type);
    return t?.text_color || '#334155';
  };

  const getEventsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const search = calendarSearch.toLowerCase().trim();
    return events.filter(e => {
      const start = e.date;
      const end = e.end_date || e.date;
      if (!(dateStr >= start && dateStr <= end)) return false;
      if (!search) return true;
      // Match same fields as job search where applicable
      if ((e.title || '').toLowerCase().includes(search)) return true;
      if ((e.type || '').toLowerCase().includes(search)) return true;
      if ((e.notes || '').toLowerCase().includes(search)) return true;
      const teamMatch = (e.assigned_to || []).some(uid => {
        const m = team.find(t => t.id === uid);
        return m && (m.full_name || '').toLowerCase().includes(search);
      });
      return teamMatch;
    });
  };

  const openNewEventDialog = (date) => {
    if (!isAdmin() && !hasPermission('manage_calendar_events')) return;
    setEditingEvent(null);
    setEventForm({
      ...emptyEventForm,
      date: format(date || new Date(), 'yyyy-MM-dd'),
    });
    setShowEventDialog(true);
  };

  const openEditEventDialog = (event, e) => {
    if (e) e.stopPropagation();
    if (!isAdmin() && !hasPermission('manage_calendar_events')) return;
    setEditingEvent(event);
    setEventForm({
      title: event.title || '',
      type: event.type || 'PTO',
      date: event.date || '',
      end_date: event.end_date && event.end_date !== event.date ? event.end_date : '',
      all_day: !!event.all_day,
      start_time: event.start_time || '',
      end_time: event.end_time || '',
      color: event.color || '',
      assigned_to: event.assigned_to || [],
      location: event.location || '',
      notes: event.notes || '',
    });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!eventForm.date) {
      toast.error('Start date is required');
      return;
    }
    setSavingEvent(true);
    try {
      const payload = {
        title: eventForm.title.trim(),
        type: eventForm.type,
        date: eventForm.date,
        end_date: eventForm.end_date || eventForm.date,
        all_day: !!eventForm.all_day,
        start_time: eventForm.all_day ? null : (eventForm.start_time || null),
        end_time: eventForm.all_day ? null : (eventForm.end_time || null),
        color: eventForm.color || null,
        assigned_to: eventForm.assigned_to,
        location: eventForm.location || null,
        notes: eventForm.notes || null,
      };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success('Event created');
      }
      setShowEventDialog(false);
      setEditingEvent(null);
      setEventForm(emptyEventForm);
      fetchEvents();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save event');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    if (!window.confirm(`Delete event "${editingEvent.title}"?`)) return;
    try {
      await axios.delete(`${API}/events/${editingEvent.id}`);
      toast.success('Event deleted');
      setShowEventDialog(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete event');
    }
  };

  // Hover-card rich content renderers
  const renderJobHoverContent = (job) => {
    const statusCls = getStatusColor(job.status) || 'bg-slate-100 text-slate-700';
    const cleanTitle = (job.daily_series_position && job.daily_series_total > 1)
      ? (job.job_title || '').replace(/\s+—\s+Day\s+\d+\s*$/, '')
      : job.job_title;
    const dayBadge = job.daily_series_position && job.daily_series_total > 1
      ? `Day ${job.daily_series_position} of ${job.daily_series_total}`
      : null;
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1.5 min-w-0">
            {job.job_number && (
              <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                {job.job_number}
              </span>
            )}
            {dayBadge && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                {dayBadge}
              </span>
            )}
          </div>
          {job.status && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${statusCls}`} style={getStatusStyle(job.status) || undefined}>
              {job.status}
            </span>
          )}
        </div>
        <div className="font-semibold text-slate-900 leading-snug">
          {cleanTitle || 'Untitled Job'}
        </div>
        {job.client_name && (
          <div className="text-slate-700 text-xs">
            <span className="text-slate-500">Client:</span> {job.client_name}
          </div>
        )}
        {job.service_address && (
          <div className="text-slate-700 text-xs leading-snug">
            <span className="text-slate-500">Address:</span> {job.service_address}
          </div>
        )}
        {job.time && (
          <div className="text-slate-700 text-xs">
            <span className="text-slate-500">Time:</span> {job.time}
          </div>
        )}
        {job.assigned_team_members?.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-slate-500 text-xs">Team:</span>
            <div className="flex -space-x-1">
              {job.assigned_team_members.slice(0, 6).map(memberId => {
                const m = getMemberInfo(memberId);
                if (!m) return null;
                const bg = m.calendar_color || '#94a3b8';
                return (
                  <div
                    key={memberId}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"
                    style={{ backgroundColor: bg, color: getContrastTextColor(bg) }}
                    title={m.full_name}
                  >
                    {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                );
              })}
              {job.assigned_team_members.length > 6 && (
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm bg-slate-600 text-white">
                  +{job.assigned_team_members.length - 6}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderEventHoverContent = (ev) => {
    const bg = ev.color || getEventColor(ev.type);
    const fg = ev.color ? getContrastTextColor(ev.color) : (ev.text_color || getEventTextColor(ev.type));
    const isRange = ev.end_date && ev.end_date > ev.date;
    let dateLabel = '';
    try {
      if (isRange) {
        dateLabel = `${format(parseISO(ev.date), 'MMM d')} – ${format(parseISO(ev.end_date), 'MMM d, yyyy')}`;
      } else {
        dateLabel = format(parseISO(ev.date), 'EEEE, MMM d, yyyy');
      }
    } catch {
      dateLabel = ev.date;
    }
    // Treat events without a start_time as all-day for display (handles legacy
    // events where all_day flag and start_time disagree)
    const isEffectivelyAllDay = ev.all_day || !ev.start_time;
    const timeLabel = isEffectivelyAllDay
      ? 'All day'
      : (ev.end_time ? `${formatTime12h(ev.start_time)} – ${formatTime12h(ev.end_time)}` : formatTime12h(ev.start_time));
    return (
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide"
            style={{ backgroundColor: bg, color: fg }}
          >
            {ev.type}
          </span>
        </div>
        <div className="font-semibold text-slate-900 leading-snug">{ev.title}</div>
        <div className="text-slate-700 text-xs">{dateLabel}</div>
        <div className="text-slate-700 text-xs">
          <span className="text-slate-500">When:</span> {timeLabel}
        </div>
        {ev.assigned_to?.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <span className="text-slate-500 text-xs">Team:</span>
            <div className="flex -space-x-1">
              {ev.assigned_to.slice(0, 6).map(uid => {
                const m = getMemberInfo(uid);
                if (!m) return null;
                const memberBg = m.calendar_color || '#94a3b8';
                return (
                  <div
                    key={uid}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"
                    style={{ backgroundColor: memberBg, color: getContrastTextColor(memberBg) }}
                    title={m.full_name}
                  >
                    {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                );
              })}
              {ev.assigned_to.length > 6 && (
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm bg-slate-600 text-white">
                  +{ev.assigned_to.length - 6}
                </div>
              )}
            </div>
          </div>
        )}
        {ev.notes && (
          <div className="text-xs text-slate-600 pt-1 border-t border-slate-100 leading-snug">
            {ev.notes}
          </div>
        )}
      </div>
    );
  };

  // Event drag-and-drop handlers (admin-only — same gate as create/edit)
  const handleEventDragStart = (e, ev) => {
    if (!isAdmin()) {
      e.preventDefault();
      return;
    }
    draggedEvent = ev;
    setDraggingEvent(ev);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `event:${ev.id}`);
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleEventDragEnd = (e) => {
    e.target.style.opacity = '1';
    draggedEvent = null;
    setDraggingEvent(null);
    setDragOverDate(null);
  };

  const handleEventDrop = async (targetDate) => {
    const ev = draggedEvent;
    if (!ev || !isAdmin()) return false;
    const newDateStr = format(targetDate, 'yyyy-MM-dd');
    if (newDateStr === ev.date) return true; // consumed but no-op

    // Preserve range length if it was a multi-day event
    let newEndDate = newDateStr;
    if (ev.end_date && ev.end_date > ev.date) {
      const oldStart = parseISO(ev.date);
      const oldEnd = parseISO(ev.end_date);
      const days = Math.round((oldEnd - oldStart) / (24 * 60 * 60 * 1000));
      const newEnd = new Date(parseISO(newDateStr).getTime() + days * 24 * 60 * 60 * 1000);
      newEndDate = format(newEnd, 'yyyy-MM-dd');
    }

    // Optimistic UI
    setEvents(prev => prev.map(x =>
      x.id === ev.id ? { ...x, date: newDateStr, end_date: newEndDate } : x
    ));
    try {
      await axios.put(`${API}/events/${ev.id}`, { date: newDateStr, end_date: newEndDate });
      toast.success(`Moved "${ev.title}" to ${format(targetDate, 'MMM d')}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to move event');
      fetchEvents(); // revert
    }
    return true;
  };

  const fetchReferenceData = async () => {
    try {
      const response = await axios.get(`${API}/reference-data`);
      const data = response.data;
      setTeam(data.team || []);
      setServices(data.services || []);
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
    }
  };

  const matchesSearch = (job) => {
    const search = calendarSearch.toLowerCase().trim();
    if (!search) return true;
    return (
      (job.job_title || '').toLowerCase().includes(search) ||
      (job.client_name || '').toLowerCase().includes(search) ||
      (job.job_number || '').toLowerCase().includes(search) ||
      (job.service_address || '').toLowerCase().includes(search) ||
      (job.service_street || '').toLowerCase().includes(search) ||
      (job.service_city || '').toLowerCase().includes(search) ||
      (job.service_type || '').toLowerCase().includes(search) ||
      (job.contact_name || '').toLowerCase().includes(search) ||
      (job.status || '').toLowerCase().includes(search) ||
      (job.assigned_team_members || []).some(id => {
        const m = team.find(t => t.id === id);
        return m && (m.full_name || '').toLowerCase().includes(search);
      })
    );
  };

  // Get all matching dates for search navigation
  const getSearchMatchDates = () => {
    if (!calendarSearch.trim()) return [];
    const dates = new Set();
    jobs.forEach(job => {
      if (!matchesSearch(job)) return;
      // If job has end_date, add every day in the range
      if (job.date && job.end_date && job.end_date > job.date) {
        let cursor = parseISO(job.date);
        const endDate = parseISO(job.end_date);
        while (cursor <= endDate) {
          dates.add(format(cursor, 'yyyy-MM-dd'));
          cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000);
        }
      } else if (job.date) {
        dates.add(job.date);
      }
      if (job.is_multi_day && job.job_days) {
        job.job_days.forEach(d => { if (d.date) dates.add(d.date); });
      }
    });
    return Array.from(dates).sort();
  };

  const searchMatchDates = calendarSearch.trim() ? getSearchMatchDates() : [];
  const currentDateStr = format(selectedDate, 'yyyy-MM-dd');
  const currentMatchIndex = searchMatchDates.findIndex(d => d >= currentDateStr);

  const jumpToFirstMatch = () => {
    if (searchMatchDates.length > 0) {
      setSelectedDate(parseISO(searchMatchDates[0]));
    }
  };

  const jumpToNextMatch = () => {
    const idx = searchMatchDates.findIndex(d => d > currentDateStr);
    if (idx >= 0) {
      setSelectedDate(parseISO(searchMatchDates[idx]));
    }
  };

  const jumpToPrevMatch = () => {
    const reversed = [...searchMatchDates].reverse();
    const match = reversed.find(d => d < currentDateStr);
    if (match) {
      setSelectedDate(parseISO(match));
    }
  };

  const getJobsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const result = [];

    jobs.forEach(job => {
      const jobStart = job.date;
      const jobEnd = job.end_date || job.date;
      const hasEndDate = !!job.end_date && job.end_date > job.date;
      const inRange = dateStr >= jobStart && dateStr <= jobEnd;

      // PRIMARY: if end_date is set, span the range (ignore legacy is_multi_day flag)
      if (hasEndDate) {
        if (inRange && matchesSearch(job)) {
          result.push(job);
        }
        return;
      }

      // LEGACY: job_days-based multi-day jobs (no end_date set)
      if (job.is_multi_day && job.job_days && job.job_days.length > 0) {
        if (job.date === dateStr && matchesSearch(job)) {
          result.push({
            ...job,
            display_title: `${job.job_number} - Day 1`,
            day_number: 1,
            day_session: null
          });
        }
        job.job_days.forEach(daySession => {
          if (daySession.date === dateStr && matchesSearch(job)) {
            result.push({
              ...job,
              date: daySession.date,
              time: daySession.time || job.time,
              assigned_team_members: daySession.assigned_to || job.assigned_team_members,
              display_title: `${job.job_number} - Day ${daySession.day_number}`,
              day_number: daySession.day_number,
              day_session: daySession,
              day_notes: daySession.notes,
              day_status: daySession.status
            });
          }
        });
        return;
      }

      // Single-day job
      if (inRange && matchesSearch(job)) {
        result.push(job);
      }
    });
    
    // Sort: jobs with daily_order first (by order), then A-Z by client name
    result.sort((a, b) => {
      const orderA = a.daily_order?.[dateStr];
      const orderB = b.daily_order?.[dateStr];
      const hasA = orderA !== undefined && orderA !== null;
      const hasB = orderB !== undefined && orderB !== null;
      
      if (hasA && hasB) return orderA - orderB;
      if (hasA) return -1;
      if (hasB) return 1;
      
      const nameA = (a.client_name || '').toLowerCase();
      const nameB = (b.client_name || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      return (a.job_title || '').toLowerCase().localeCompare((b.job_title || '').toLowerCase());
    });
    
    return result;
  };

  const getImportedEventsForDate = (date) => {    if (!showLegacy) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    return importedEvents.filter(event => event.start_date === dateStr);
  };

  const handleImportICS = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.ics')) {
      toast.error('Please select an .ics file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', 'teamup');

    try {
      const response = await axios.post(`${API}/imported-events/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      if (result.success) {
        toast.success(`Imported ${result.imported_count} events (${result.skipped_count} skipped)`);
        setShowImportDialog(false);
        fetchImportedEvents();
        fetchImportStats();
        setShowLegacy(true);
      } else {
        toast.error(result.errors?.[0] || 'Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleLegacyEventClick = (event, e) => {
    e?.stopPropagation();
    setSelectedLegacyEvent(event);
    setShowLegacyDetailDialog(true);
  };

  const navigatePrevious = () => {
    if (viewMode === 'month') setSelectedDate(subMonths(selectedDate, 1));
    else if (viewMode === 'week') setSelectedDate(subWeeks(selectedDate, 1));
    else if (viewMode === '3day') setSelectedDate(subDays(selectedDate, 3));
    else if (viewMode === '6day') setSelectedDate(subDays(selectedDate, 6));
    else if (viewMode === '3week') setSelectedDate(subWeeks(selectedDate, 3));
    else if (viewMode === '3month') setSelectedDate(subMonths(selectedDate, 3));
    else setSelectedDate(subDays(selectedDate, 1));
  };

  const navigateNext = () => {
    if (viewMode === 'month') setSelectedDate(addMonths(selectedDate, 1));
    else if (viewMode === 'week') setSelectedDate(addWeeks(selectedDate, 1));
    else if (viewMode === '3day') setSelectedDate(addDays(selectedDate, 3));
    else if (viewMode === '6day') setSelectedDate(addDays(selectedDate, 6));
    else if (viewMode === '3week') setSelectedDate(addWeeks(selectedDate, 3));
    else if (viewMode === '3month') setSelectedDate(addMonths(selectedDate, 3));
    else setSelectedDate(addDays(selectedDate, 1));
  };

  const getDateRange = () => {
    if (viewMode === 'month') {
      return format(selectedDate, 'MMMM yyyy');
    } else if (viewMode === 'week') {
      const start = startOfWeek(selectedDate);
      const end = endOfWeek(selectedDate);
      const weekNum = getWeek(selectedDate);
      return `Week ${weekNum} • ${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else if (viewMode === '3day' || viewMode === '6day') {
      const days = viewMode === '3day' ? 3 : 6;
      const end = addDays(selectedDate, days - 1);
      return `${days} Days • ${format(selectedDate, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else if (viewMode === '3week') {
      const start = startOfWeek(selectedDate);
      const end = endOfWeek(addWeeks(selectedDate, 2));
      return `3 Weeks • ${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
    } else if (viewMode === '3month') {
      const start = startOfMonth(selectedDate);
      const end = endOfMonth(addMonths(selectedDate, 2));
      return `3 Months • ${format(start, 'MMM yyyy')} - ${format(end, 'MMM yyyy')}`;
    } else if (viewMode === 'custom') {
      if (customRangeStart && customRangeEnd) {
        return `${format(parseISO(customRangeStart), 'MMM d')} - ${format(parseISO(customRangeEnd), 'MMM d, yyyy')}`;
      }
      return 'Select date range';
    } else {
      const weekNum = getWeek(selectedDate);
      return `Week ${weekNum} • ${format(selectedDate, 'EEEE, MMMM d, yyyy')}`;
    }
  };

  const getDaysToShow = () => {
    if (viewMode === 'month') {
      const start = startOfWeek(startOfMonth(selectedDate));
      const end = endOfWeek(endOfMonth(selectedDate));
      return eachDayOfInterval({ start, end });
    } else if (viewMode === 'week') {
      return eachDayOfInterval({
        start: startOfWeek(selectedDate),
        end: endOfWeek(selectedDate)
      });
    } else if (viewMode === '3day' || viewMode === '6day') {
      const days = viewMode === '3day' ? 3 : 6;
      return eachDayOfInterval({
        start: selectedDate,
        end: addDays(selectedDate, days - 1)
      });
    } else if (viewMode === '3week') {
      return eachDayOfInterval({
        start: startOfWeek(selectedDate),
        end: endOfWeek(addWeeks(selectedDate, 2))
      });
    } else if (viewMode === '3month') {
      const start = startOfWeek(startOfMonth(selectedDate));
      const end = endOfWeek(endOfMonth(addMonths(selectedDate, 2)));
      return eachDayOfInterval({ start, end });
    } else if (viewMode === 'custom') {
      if (customRangeStart && customRangeEnd) {
        return eachDayOfInterval({
          start: parseISO(customRangeStart),
          end: parseISO(customRangeEnd)
        });
      }
      return [];
    } else {
      return [selectedDate];
    }
  };

  const getMemberInfo = (memberId) => {
    return team.find(m => m.id === memberId);
  };

  // Handle clicking on an empty date to create a new job
  const handleDateClick = (date) => {
    if (!canCreateJobs) return;
    
    setIsEditMode(false);
    setSelectedJob(null);
    setJobForm({
      ...emptyJobForm,
      date: format(date, 'yyyy-MM-dd')
    });
    setShowJobDialog(true);
  };

  // Handle clicking on a job to edit it
  const handleJobClick = (job, e) => {
    e.stopPropagation();

    // On mobile, open the Day Sheet for the job's date — user picks the right job from a clear list,
    // which opens the Job Card. Removes mis-tap risk on tiny chips.
    if (isMobile || window.innerWidth < 768) {
      const jobDate = job.date || (job.job_days && job.job_days[0]?.date);
      if (jobDate) {
        try {
          setDaySheetDate(parseISO(jobDate));
          setShowDaySheet(true);
          setV2JobsExpanded(false);
          return;
        } catch (_) {
          // fall through to legacy action dialog
        }
      }
      setSelectedJob(job);
      setShowMobileActionDialog(true);
      return;
    }

    // Desktop unchanged: navigate to full job detail page
    navigate(`/jobs/${job.id}`, { state: { from: 'calendar' } });
  };
  
  // Quick edit from calendar (optional - keeps the dialog for fast date/time changes)
  const openQuickEdit = (job) => {
    setIsEditMode(true);
    setSelectedJob(job);
    setJobForm({
      client_name: job.client_name || '',
      phone: job.phone || '',
      email: job.email || '',
      service_address: job.service_address || '',
      service_street: job.service_street || '',
      service_city: job.service_city || '',
      service_state: job.service_state || '',
      service_zip: job.service_zip || '',
      job_title: job.job_title || '',
      service_type: job.service_type || '',
      date: job.date || '',
      time: job.time || '',
      duration: job.duration || '',
      priority: job.priority || 'Normal',
      status: job.status || 'Scheduled',
      notes: job.notes || '',
      assigned_team_members: job.assigned_team_members || []
    });
    setShowJobDialog(true);
  };

  // Open job for editing - now navigates to full page
  const openJobForEdit = (job) => {
    navigate(`/jobs/${job.id}`, { state: { from: 'calendar' } });
  };

  // Handle delete button click
  const handleDeleteClick = (job, e) => {
    e.stopPropagation();
    setSelectedJob(job);
    setShowDeleteDialog(true);
  };

  // Save job (create or update)
  const handleSaveJob = async ({ openFullEditor = false } = {}) => {
    if (!jobForm.client_name || !jobForm.job_title || !jobForm.service_type || !jobForm.date) {
      toast.error('Please fill in required fields: Client Name, Job Title, Service Type, and Date');
      return;
    }

    // Build service address from components if not set
    let serviceAddress = jobForm.service_address;
    if (!serviceAddress && jobForm.service_street) {
      serviceAddress = [
        jobForm.service_street,
        jobForm.service_city,
        jobForm.service_state,
        jobForm.service_zip
      ].filter(Boolean).join(', ');
    }

    const payload = {
      ...jobForm,
      service_address: serviceAddress || jobForm.service_street || 'TBD',
      duration: jobForm.duration ? parseFloat(jobForm.duration) : null
    };

    // Prompt 28 (Feb 2026): the New Job dialog defaults status to
    // "Scheduled", but the backend now rejects a dispatch-meaning status
    // (Ready to schedule / Scheduled / In Progress) with no street — which
    // would otherwise dead-end every quick "capture now, fill in the
    // address later" job created from the calendar. Mirrors the same
    // fallback convert_to_job uses: no street yet -> start as Pending.
    // Only applies to brand-new jobs; editing an existing job never
    // silently changes the status the user explicitly set.
    if (!isEditMode && !jobForm.service_street && ['Ready to schedule', 'Scheduled', 'In Progress'].includes(payload.status)) {
      payload.status = 'Pending';
    }

    // Prompt 40: tell the user when a new job is ending up Pending
    // specifically because the address is missing (same wording/condition
    // as the equivalent notice on the Jobs page new-job flow).
    const createdAsPendingForAddress = !isEditMode && !jobForm.service_street && payload.status === 'Pending';

    setSaving(true);
    try {
      let savedJob = null;
      if (isEditMode && selectedJob) {
        const r = await axios.put(`${API}/jobs/${selectedJob.id}`, payload);
        savedJob = r.data;
        toast.success('Job updated successfully');
      } else {
        const r = await axios.post(`${API}/jobs`, payload);
        savedJob = r.data;
        toast.success(createdAsPendingForAddress ? 'Created as Pending: site address needed' : 'Job created successfully');
      }
      setShowJobDialog(false);
      // Two outcomes:
      //   - openFullEditor=true → user clicked "Save & Open Full Editor".
      //     Navigate to the full Edit Job page so they get the full UI
      //     (Day strip, photo upload, full notes, etc.) without losing
      //     anything they typed.
      //   - default → original behavior, stay on calendar and refresh.
      if (openFullEditor && savedJob?.id) {
        navigate(`/jobs/${savedJob.id}`, { state: { from: 'calendar' } });
      } else {
        fetchJobs();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setSaving(false);
    }
  };

  // Delete job
  const handleDeleteJob = async () => {
    if (!selectedJob) return;

    try {
      await axios.delete(`${API}/jobs/${selectedJob.id}`);
      toast.success('Job deleted successfully');
      setShowDeleteDialog(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  // Handle team member toggle
  const handleTeamMemberToggle = (memberId) => {
    setJobForm(prev => ({
      ...prev,
      assigned_team_members: prev.assigned_team_members.includes(memberId)
        ? prev.assigned_team_members.filter(id => id !== memberId)
        : [...prev.assigned_team_members, memberId]
    }));
  };

  // Handle contact selection to auto-fill fields
  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      // Prompt 41: only fill client identity — never the service/site
      // address. A typed site address must survive regardless of which
      // contact gets picked afterward.
      setJobForm(prev => ({
        ...prev,
        client_name: contact.company_name || contact.client_name || contact.contact_name || '',
        phone: contact.phone1 || contact.phone || '',
        email: contact.email || '',
      }));
      toast.success(`Filled from: ${contact.company_name || contact.client_name || 'Client'}`);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, job) => {
    if (!canEditThisJob(job)) {
      e.preventDefault();
      return;
    }
    draggedJob = job;
    setDraggingJob(job);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.id);
    // Add visual feedback
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    draggedJob = null;
    setDraggingJob(null);
    setDragOverDate(null);
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const dateStr = format(date, 'yyyy-MM-dd');
    if (dragOverDate !== dateStr) {
      setDragOverDate(dateStr);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the cell entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverDate(null);
    }
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    setDragOverDate(null);
    
    // Event drop takes precedence when an event is being dragged
    if (draggedEvent) {
      await handleEventDrop(targetDate);
      return;
    }

    const job = draggedJob;
    if (!job || !canEditThisJob(job)) return;
    
    const newDateStr = format(targetDate, 'yyyy-MM-dd');
    const oldDateStr = job.date;
    
    // Don't do anything if dropping on the same date
    if (newDateStr === oldDateStr) {
      setDraggingJob(null);
      return;
    }

    // Check if this is a day session (Day 2, Day 3, etc.) of a multi-day job
    if (job.day_session && job.day_session.id) {
      // Update the specific day session, not the main job
      const dayId = job.day_session.id;
      const dayNumber = job.day_number;
      
      // Optimistically update UI
      setJobs(prev => prev.map(j => {
        if (j.id === job.id && j.is_multi_day && j.job_days) {
          return {
            ...j,
            job_days: j.job_days.map(d => 
              d.id === dayId ? { ...d, date: newDateStr } : d
            )
          };
        }
        return j;
      }));
      setDraggingJob(null);

      try {
        await axios.put(`${API}/jobs/${job.id}/days/${dayId}`, {
          date: newDateStr
        });
        toast.success(`Day ${dayNumber} rescheduled to ${format(targetDate, 'MMM d, yyyy')}`);
        // Refresh jobs to get updated data
        fetchJobs();
      } catch (error) {
        // Revert on error
        fetchJobs();
        toast.error('Failed to reschedule day session');
        console.error('Reschedule day error:', error);
      }
      return;
    }

    // For main job (Day 1) or single-day jobs
    // Optimistically update UI
    setJobs(prev => prev.map(j => 
      j.id === job.id ? { ...j, date: newDateStr } : j
    ));
    setDraggingJob(null);

    try {
      await axios.put(`${API}/jobs/${job.id}`, {
        ...job,
        date: newDateStr
      });
      toast.success(`Job rescheduled to ${format(targetDate, 'MMM d, yyyy')}`);
    } catch (error) {
      // Revert on error
      setJobs(prev => prev.map(j => 
        j.id === job.id ? { ...j, date: oldDateStr } : j
      ));
      toast.error(error.response?.data?.detail || 'Failed to reschedule job');
      console.error('Reschedule error:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 animate-pulse rounded" />
        <div className="h-96 bg-slate-200 animate-pulse rounded-lg" />
      </div>
    );
  }

  const days = getDaysToShow();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header — title on the left, toolbar on the right. We use
          `flex-wrap items-start` so when the toolbar wraps to multiple rows
          on narrow viewports, the title stays top-aligned with the FIRST
          toolbar row instead of being pushed below. */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex items-center gap-3 flex-shrink-0 h-9">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight leading-none">
            Calendar
          </h1>
          {/* V1/V2 layout toggle — segmented control, matches the other
              h-9 toolbar toggles (Compact/Expanded, Job Numbers, etc.). */}
          <div
            className="flex items-center h-9 border border-slate-200 rounded-md overflow-hidden bg-white"
            role="group"
            aria-label="Calendar layout"
            data-testid="calendar-layout-toggle"
          >
            <button
              type="button"
              onClick={async () => {
                if (isV2Calendar && typeof updatePreference === 'function') {
                  await updatePreference('calendar_layout', 'v1');
                }
              }}
              className={`h-9 px-3 text-sm font-medium transition-colors ${
                !isV2Calendar
                  ? 'bg-slate-800 text-white hover:bg-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              aria-pressed={!isV2Calendar}
              data-testid="calendar-layout-v1-btn"
              title="V1 — classic month grid"
            >
              V1
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!isV2Calendar && typeof updatePreference === 'function') {
                  await updatePreference('calendar_layout', 'v2');
                }
              }}
              className={`h-9 px-3 text-sm font-medium transition-colors border-l border-slate-200 ${
                isV2Calendar
                  ? 'bg-slate-800 text-white hover:bg-slate-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              aria-pressed={isV2Calendar}
              data-testid="calendar-layout-v2-btn"
              title="V2 — mini-month + day list"
            >
              V2
            </button>
          </div>
          {isMobile && (
            <button
              type="button"
              onClick={() => setV2HeaderExpanded((v) => !v)}
              className="h-9 px-3 text-sm font-medium border border-slate-200 rounded-md bg-white text-slate-600 hover:bg-slate-50"
              data-testid="v2-toolbar-toggle"
              aria-expanded={v2HeaderExpanded}
            >
              {v2HeaderExpanded ? 'Hide' : 'Options'}
            </button>
          )}
        </div>
        <div
          className={`flex items-center gap-3 flex-wrap ${
            isMobile && !v2HeaderExpanded ? 'hidden' : ''
          }`}
        >
          {/* Calendar Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search jobs..."
              value={calendarSearch}
              onChange={(e) => setCalendarSearch(e.target.value)}
              className="pl-9 w-[180px] md:w-[220px] h-9"
              data-testid="calendar-search-input"
            />
            {calendarSearch && (
              <button
                onClick={() => setCalendarSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Select value={viewMode} onValueChange={(v) => {
            setViewMode(v);
            // Team-up style: when the user picks a relative range from the
            // dropdown, anchor it to TODAY so e.g. "3 Days" shows today + 2.
            if (v === '3day' || v === '6day' || v === '3week' || v === '3month') {
              setSelectedDate(new Date());
            }
          }}>
            <SelectTrigger className="w-[140px] h-9" data-testid="view-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="3day">3 Days</SelectItem>
              <SelectItem value="6day">6 Days</SelectItem>
              <SelectItem value="3week">3 Weeks</SelectItem>
              <SelectItem value="3month">3 Months</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>

          {/* Reset height button — only visible when user has dragged the
              day-cell height away from the default for the current view. */}
          {isCellHeightOverridden && (
            <Button
              variant="outline"
              size="sm"
              onClick={resetCellHeight}
              className="h-9 text-slate-600 hover:text-amber-600"
              data-testid="reset-cell-height-btn"
              title="Reset day-cell height to default for this view"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Reset
            </Button>
          )}

          {/* Compact/Expanded Toggle */}
          {viewMode !== 'day' && (
            <div className="flex items-center h-9 border border-slate-200 rounded-md overflow-hidden">
              <Button
                variant={calendarDisplayMode === 'compact' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarDisplayMode('compact')}
                className={`h-9 rounded-none ${calendarDisplayMode === 'compact' ? 'bg-slate-800 text-white hover:bg-slate-700' : 'text-slate-600'}`}
                data-testid="calendar-compact-btn"
              >
                Compact
              </Button>
              <Button
                variant={calendarDisplayMode === 'expanded' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setCalendarDisplayMode('expanded')}
                className={`h-9 rounded-none ${calendarDisplayMode === 'expanded' ? 'bg-slate-800 text-white hover:bg-slate-700' : 'text-slate-600'}`}
                data-testid="calendar-expanded-btn"
              >
                Expanded
              </Button>
            </div>
          )}

          {/* Job Number Toggle - admins only (regular users shouldn't be flipping global view) */}
          {isAdmin() && (
            <Button
              variant={showJobNumbers ? 'default' : 'outline'}
              size="sm"
              onClick={toggleJobNumbers}
              className={`h-9 ${showJobNumbers ? 'bg-slate-800 text-white hover:bg-slate-700' : 'text-slate-600'}`}
              data-testid="toggle-job-numbers-btn"
              title={showJobNumbers ? 'Hide job numbers' : 'Show job numbers'}
            >
              <span className="font-mono">#</span>
              <span className="ml-1 text-xs">{showJobNumbers ? 'On' : 'Off'}</span>
            </Button>
          )}

          {(isAdmin() || hasPermission('manage_calendar_events')) && (
            <Button
              size="sm"
              className="h-9 bg-slate-900 hover:bg-slate-800 text-white font-bold"
              onClick={() => openNewEventDialog(new Date())}
              data-testid="calendar-new-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Event
            </Button>
          )}

          {canCreateJobs && (
            <Button
              size="sm"
              className="h-9 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={() => handleDateClick(new Date())}
              data-testid="calendar-new-job-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
          )}

          {/* Overflow menu — less-used controls */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9 w-9 p-0 text-slate-600"
                data-testid="calendar-overflow-btn"
                title="More options"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuCheckboxItem
                checked={showLegacy}
                onCheckedChange={setShowLegacy}
                data-testid="show-legacy-toggle"
              >
                <Archive className="w-4 h-4 mr-2 text-slate-500" />
                Show Legacy Jobs
                {importStats?.total_events > 0 && (
                  <span className="ml-auto text-xs text-slate-500">({importStats.total_events})</span>
                )}
              </DropdownMenuCheckboxItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowImportDialog(true)}
                data-testid="import-ics-btn"
              >
                <Upload className="w-4 h-4 mr-2 text-slate-500" />
                Import ICS file…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div
        className={`flex items-center justify-between ${
          isV2Calendar ? 'hidden' : ''
        }`}
      >
        {viewMode !== 'custom' ? (
          <>
            <Button variant="outline" onClick={navigatePrevious}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-semibold text-slate-900">{getDateRange()}</h2>
              {canEditAllJobs && viewMode !== 'day' && (
                <p className="text-xs text-slate-500 mt-1 flex items-center justify-center gap-1">
                  <GripVertical className="w-3 h-3" />
                  Drag jobs to reschedule
                </p>
              )}
            </div>
            <Button variant="outline" onClick={navigateNext}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-3 w-full justify-center flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">From</label>
              <Input
                type="date"
                value={customRangeStart}
                onChange={(e) => setCustomRangeStart(e.target.value)}
                className="w-[160px]"
                data-testid="custom-range-start"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-600">To</label>
              <Input
                type="date"
                value={customRangeEnd}
                onChange={(e) => setCustomRangeEnd(e.target.value)}
                className="w-[160px]"
                data-testid="custom-range-end"
              />
            </div>
            {customRangeStart && customRangeEnd && (
              <span className="text-sm text-slate-500">{getDateRange()}</span>
            )}
          </div>
        )}
      </div>

      {/* Search Results List — replaces calendar grid when searching */}
      {calendarSearch.trim() ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {(() => {
              const matchingJobs = jobs.filter(matchesSearch).sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              const search = calendarSearch.toLowerCase().trim();
              const matchingEvents = events
                .filter(e => {
                  if ((e.title || '').toLowerCase().includes(search)) return true;
                  if ((e.type || '').toLowerCase().includes(search)) return true;
                  if ((e.notes || '').toLowerCase().includes(search)) return true;
                  return (e.assigned_to || []).some(uid => {
                    const m = team.find(t => t.id === uid);
                    return m && (m.full_name || '').toLowerCase().includes(search);
                  });
                })
                .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              const totalMatches = matchingJobs.length + matchingEvents.length;
              if (totalMatches === 0) {
                return (
                  <div className="p-8 text-center text-slate-500">
                    No jobs or events found for "{calendarSearch}"
                  </div>
                );
              }
              return (
                <>
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">
                      {totalMatches} {totalMatches === 1 ? 'result' : 'results'} found
                      {matchingEvents.length > 0 && matchingJobs.length > 0 && (
                        <span className="font-normal text-slate-500 ml-2">
                          ({matchingJobs.length} {matchingJobs.length === 1 ? 'job' : 'jobs'}, {matchingEvents.length} {matchingEvents.length === 1 ? 'event' : 'events'})
                        </span>
                      )}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setCalendarSearch('')} className="text-xs text-slate-500">
                      Clear search
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                    {matchingEvents.map(ev => {
                      const bg = ev.color || getEventColor(ev.type);
                      const fg = getEventTextColor(ev.type);
                      return (
                        <div
                          key={`event-result-${ev.id}`}
                          className="flex items-center gap-4 px-4 py-3 hover:bg-amber-50 cursor-pointer transition-colors"
                          onClick={() => openEditEventDialog(ev)}
                          data-testid={`search-result-event-${ev.id}`}
                        >
                          <div className="text-center flex-shrink-0 w-16">
                            <div className="text-xs text-slate-500">{ev.date ? format(parseISO(ev.date), 'MMM') : ''}</div>
                            <div className="text-lg font-bold text-slate-800">{ev.date ? format(parseISO(ev.date), 'd') : ''}</div>
                            <div className="text-xs text-slate-400">{ev.date ? format(parseISO(ev.date), 'EEE') : ''}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                                style={{ backgroundColor: bg, color: fg }}
                              >
                                {ev.type}
                              </span>
                              <span className="font-semibold text-slate-900 truncate">{ev.title}</span>
                              {ev.all_day ? (
                                <span className="text-[10px] text-slate-500">All Day</span>
                              ) : ev.start_time ? (
                                <span className="text-[10px] text-slate-500">{formatTime12h(ev.start_time)}{ev.end_time ? ` – ${formatTime12h(ev.end_time)}` : ''}</span>
                              ) : null}
                            </div>
                            {ev.end_date && ev.end_date !== ev.date && (
                              <div className="text-xs text-slate-500">{format(parseISO(ev.date), 'MMM d')} – {format(parseISO(ev.end_date), 'MMM d, yyyy')}</div>
                            )}
                            {ev.notes && (
                              <div className="text-xs text-slate-400 truncate">{ev.notes}</div>
                            )}
                          </div>
                          {ev.assigned_to?.length > 0 && (
                            <div className="flex -space-x-1 flex-shrink-0">
                              {ev.assigned_to.slice(0, 3).map(uid => {
                                const m = getMemberInfo(uid);
                                const bg = m?.calendar_color || '#94a3b8';
                                return (
                                  <div
                                    key={uid}
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"
                                    style={{ backgroundColor: bg, color: getContrastTextColor(bg) }}
                                    title={m?.full_name}
                                  >
                                    {m?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                        </div>
                      );
                    })}
                    {matchingJobs.map(job => (
                      <div
                        key={job.id}
                        className="flex items-center gap-4 px-4 py-3 hover:bg-amber-50 cursor-pointer transition-colors"
                        onClick={() => {
                          navigate(`/jobs/${job.id}`);
                        }}
                        data-testid={`search-result-${job.id}`}
                      >
                        <div className="text-center flex-shrink-0 w-16">
                          <div className="text-xs text-slate-500">{job.date ? format(parseISO(job.date), 'MMM') : ''}</div>
                          <div className="text-lg font-bold text-slate-800">{job.date ? format(parseISO(job.date), 'd') : ''}</div>
                          <div className="text-xs text-slate-400">{job.date ? format(parseISO(job.date), 'EEE') : ''}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900 truncate">{job.job_title || 'Untitled'}</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(job.status)}`} style={getStatusStyle(job.status) || undefined}>{job.status}</span>
                          </div>
                          <div className="text-sm text-slate-600 truncate">{job.client_name}</div>
                          <div className="text-xs text-slate-400 truncate">
                            {[job.service_street, job.service_city, job.service_state].filter(Boolean).join(', ') || job.service_address || ''}
                          </div>
                        </div>
                        {job.assigned_team_members?.length > 0 && (
                          <div className="flex -space-x-1 flex-shrink-0">
                            {job.assigned_team_members.slice(0, 3).map(memberId => {
                              const member = getMemberInfo(memberId);
                              const bgColor = member?.calendar_color || '#f59e0b';
                              const textColor = getContrastTextColor(bgColor);
                              return (
                                <div
                                  key={memberId}
                                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"
                                  style={{ backgroundColor: bgColor, color: textColor }}
                                  title={member?.full_name}
                                >
                                  {member?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      ) : (
      <>
      {/* V2 Calendar — opt-in via Profile → Display Preferences or the
          layout pill in the header. Renders on every screen size and
          ignores `viewMode` (it has its own mini-month + day list
          paradigm — the view-range dropdown is a V1-only concept). */}
      {isV2Calendar && (
        <V2MobileCalendar
          cursorMonth={selectedDate}
          onPrevMonth={navigatePrevious}
          onNextMonth={navigateNext}
          onJumpToToday={() => setSelectedDate(new Date())}
          selectedDay={daySheetDate || selectedDate}
          onSelectDay={(d) => {
            setDaySheetDate(d);
            setV2JobsExpanded(false);
            setSelectedDate(d);
          }}
          getJobsForDate={getJobsForDate}
          getEventsForDate={getEventsForDate}
          getEventColor={getEventColor}
          getEventTextColor={getEventTextColor}
          getContrastTextColor={getContrastTextColor}
          onOpenEvent={(ev) => {
            // Mobile V2: route through the same edit helper the desktop
            // uses so the form gets PRE-FILLED with the event's existing
            // values. Previously this just set editingEvent + opened the
            // dialog without populating eventForm — the form looked blank
            // and users thought they had to retype everything.
            openEditEventDialog(ev);
          }}
          onOpenJobsList={() => {
            // Reuse the existing V1 day-sheet dialog: it already lists every
            // job for the day with status pills, addresses, team avatars,
            // etc. Tapping a job opens the existing V1 job action dialog.
            // IMPORTANT: ensure `daySheetDate` is set before opening the
            // dialog — the dialog body renders only when daySheetDate is
            // truthy. On first load today is shown via the
            // `daySheetDate || selectedDate` fallback but daySheetDate
            // itself is still null, so opening the dialog without this
            // line shows an empty gray screen.
            if (!daySheetDate) setDaySheetDate(selectedDate);
            setV2JobsExpanded(true);
            setShowDaySheet(true);
          }}
        />
      )}

      {/* Calendar Grid */}
      <Card className={`border-slate-200 overflow-hidden ${isV2Calendar ? 'hidden' : ''}`}>
        {(() => {
          // Compute grid column count per view mode.
          // 3day → 3 cols (Sun-aligned dates instead of weekdays).
          // 6day → 6 cols. 3week/3month/month/week/custom → 7 cols.
          const isRangeOfDays = viewMode === '3day' || viewMode === '6day';
          const colsCls = viewMode === '3day' ? 'grid-cols-3'
                        : viewMode === '6day' ? 'grid-cols-6'
                        : 'grid-cols-7';
          const showWeekdayHeader =
            viewMode !== 'day' && !isRangeOfDays && days.length > 0;
          return (
            <>
              {showWeekdayHeader && (
                <div className={`grid ${colsCls} bg-slate-50 border-b border-slate-200`}>
                  {weekDays.map(day => (
                    <div key={day} className="p-3 text-center text-sm font-semibold text-slate-600">
                      {day}
                    </div>
                  ))}
                </div>
              )}
              {isRangeOfDays && (
                <div className={`grid ${colsCls} bg-slate-50 border-b border-slate-200`}>
                  {days.map((d, i) => (
                    <div key={i} className="p-3 text-center text-sm font-semibold text-slate-600">
                      <div>{format(d, 'EEE')}</div>
                      <div className="text-xs text-slate-500 font-normal">{format(d, 'MMM d')}</div>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        <div className={viewMode === 'day' ? '' : `grid ${
          viewMode === '3day' ? 'grid-cols-3'
          : viewMode === '6day' ? 'grid-cols-6'
          : 'grid-cols-7'
        }`}>
          {days.map((day, idx) => {
            const dayJobs = getJobsForDate(day);
            const isToday = isSameDay(day, new Date());
            // For the new fixed-range views, every cell is "in range" so we
            // always treat it as current (no faded background).
            const isCurrentMonth = (
              viewMode === 'custom'
              || viewMode === '3day' || viewMode === '6day'
              || viewMode === '3week' || viewMode === '3month'
              || day.getMonth() === selectedDate.getMonth()
            );

            if (viewMode === 'day') {
              const dayEvents = getEventsForDate(day);
              return (
                <div key={idx} className="p-4">
                  <div className="space-y-3">
                    {dayEvents.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {dayEvents.map(ev => {
                          const bg = ev.color || getEventColor(ev.type);
                          const fg = getEventTextColor(ev.type);
                          const timeLabel = !ev.all_day && ev.start_time ? ` ${formatTime12h(ev.start_time)}` : '';
                          return (
                            <div
                              key={`event-day-${ev.id}`}
                              draggable={isAdmin()}
                              onDragStart={(e) => handleEventDragStart(e, ev)}
                              onDragEnd={handleEventDragEnd}
                              onClick={(e) => openEditEventDialog(ev, e)}
                              className={`px-3 py-2 rounded-lg cursor-pointer flex items-center gap-2 ${
                                isAdmin() ? 'cursor-grab active:cursor-grabbing' : ''
                              }`}
                              style={{ backgroundColor: bg, color: fg }}
                              data-testid={`calendar-event-day-${ev.id}`}
                            >
                              <span
                                className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded"
                                style={{ backgroundColor: fg, color: bg }}
                              >
                                {ev.all_day ? 'All Day' : (formatTime12h(ev.start_time) || '')}
                              </span>
                              <span className="font-medium flex-1 min-w-0 truncate">{ev.title}{timeLabel}</span>
                              <span className="text-[10px] opacity-80">{ev.type}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {dayJobs.length === 0 ? (
                      <div className="text-center py-12 text-slate-500">
                        <p>No jobs scheduled</p>
                        {canCreateJobs && (
                          <Button 
                            variant="outline" 
                            className="mt-4"
                            onClick={() => handleDateClick(day)}
                            data-testid="calendar-add-job-empty"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Job
                          </Button>
                        )}
                      </div>
                    ) : (
                      dayJobs.map(job => (
                        <div
                          key={job.id}
                          className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-amber-500/50 transition-colors"
                        >
                          <div 
                            onClick={(e) => handleJobClick(job, e)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-start gap-2">
                                {/* Daily order number */}
                                {canEditAllJobs ? (
                                  editingOrder?.jobId === job.id && editingOrder?.date === format(day, 'yyyy-MM-dd') ? (
                                    <input
                                      type="number"
                                      min="1"
                                      max="99"
                                      className="w-8 h-8 text-sm text-center border border-amber-400 rounded bg-white focus:outline-none mt-1"
                                      defaultValue={editingOrder.value || ''}
                                      autoFocus
                                      onBlur={(e) => handleSetDailyOrder(job.id, format(day, 'yyyy-MM-dd'), e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSetDailyOrder(job.id, format(day, 'yyyy-MM-dd'), e.target.value);
                                        if (e.key === 'Escape') setEditingOrder(null);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <span
                                      className={`w-8 h-8 flex-shrink-0 flex items-center justify-center text-sm font-bold rounded cursor-pointer mt-1 ${
                                        job.daily_order?.[format(day, 'yyyy-MM-dd')]
                                          ? 'bg-amber-500 text-white'
                                          : 'bg-slate-200 text-slate-400 hover:bg-amber-100'
                                      }`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingOrder({
                                          jobId: job.id,
                                          date: format(day, 'yyyy-MM-dd'),
                                          value: job.daily_order?.[format(day, 'yyyy-MM-dd')] || ''
                                        });
                                      }}
                                      title="Click to set sort order"
                                    >
                                      {job.daily_order?.[format(day, 'yyyy-MM-dd')] || '#'}
                                    </span>
                                  )
                                ) : job.daily_order?.[format(day, 'yyyy-MM-dd')] ? (
                                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-sm font-bold rounded bg-amber-500 text-white mt-1">
                                    {job.daily_order[format(day, 'yyyy-MM-dd')]}
                                  </span>
                                ) : null}
                                <div>
                                  {showJobNumbers && job.job_number && (
                                    <span className="text-xs font-mono text-amber-600 bg-amber-50 px-2 py-0.5 rounded mb-1 inline-block">{job.job_number}</span>
                                  )}
                                  <h3 className="font-semibold text-slate-900">{job.job_title}</h3>
                                  <p className="text-sm text-slate-500">{job.client_name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className={getStatusColor(job.status)} style={getStatusStyle(job.status) || undefined}>{job.status}</Badge>
                                {canEditThisJob(job) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => handleJobClick(job, e)}
                                    title="Edit Job"
                                    data-testid={`calendar-edit-btn-${job.id}`}
                                  >
                                    <Edit className="w-4 h-4 text-blue-600" />
                                  </Button>
                                )}
                                {canDeleteJobs && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => handleDeleteClick(job, e)}
                                    title="Delete Job"
                                    data-testid={`calendar-delete-btn-${job.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-2">
                              {job.time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {job.time}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {job.service_address}
                              </span>
                            </div>
                            
                            {/* Job Attachments Display */}
                            {((job.files && job.files.length > 0) || (job.photos && job.photos.length > 0)) && (
                              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200">
                                {job.files && job.files.length > 0 && (
                                  <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                    <FileText className="w-3 h-3" />
                                    {job.files.length} file{job.files.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                                {job.photos && job.photos.length > 0 && (
                                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                                    <Image className="w-3 h-3" />
                                    {job.photos.length} photo{job.photos.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                            {job.assigned_team_members?.length > 0 ? (
                              <div className="flex -space-x-2">
                                {job.assigned_team_members.slice(0, 3).map(memberId => {
                                  const member = getMemberInfo(memberId);
                                  const bgColor = member?.calendar_color || '#f59e0b';
                                  const textColor = getContrastTextColor(bgColor);
                                  return (
                                    <div
                                      key={memberId}
                                      className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold shadow-sm"
                                      style={{ backgroundColor: bgColor, color: textColor }}
                                      title={member?.full_name}
                                    >
                                      {member?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                    </div>
                                  );
                                })}
                                {job.assigned_team_members.length > 3 && (
                                  <div 
                                    className="w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold shadow-sm bg-slate-600 text-white"
                                    title={`+${job.assigned_team_members.length - 3} more team members`}
                                  >
                                    +{job.assigned_team_members.length - 3}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">No team assigned</span>
                            )}
                            <div className="flex gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/timesheet?job_id=${job.id}&job_number=${encodeURIComponent(job.job_number || '')}&job_title=${encodeURIComponent(job.job_title)}&date=${job.date}`);
                                }}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded font-medium transition-colors"
                                title="Log Time"
                                data-testid={`calendar-timesheet-btn-${job.id}`}
                              >
                                <Timer className="w-3 h-3" />
                                Log Time
                              </button>
                              <MapButtons 
                                address={job.service_address} 
                                showLabels={false}
                                variant="ghost"
                                className="gap-0.5"
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                    
                    {/* Add Job button for Day view when there are jobs */}
                    {dayJobs.length > 0 && canCreateJobs && (
                      <Button 
                        variant="outline" 
                        className="w-full border-dashed"
                        onClick={() => handleDateClick(day)}
                        data-testid="calendar-add-job-day"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Another Job
                      </Button>
                    )}
                    
                    {/* Legacy Events for Day View */}
                    {showLegacy && getImportedEventsForDate(day).length > 0 && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                          <Archive className="w-4 h-4" />
                          Legacy Jobs ({getImportedEventsForDate(day).length})
                        </h3>
                        {getImportedEventsForDate(day).map(event => (
                          <div
                            key={`legacy-day-${event.id}`}
                            onClick={(e) => handleLegacyEventClick(event, e)}
                            className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-400 transition-colors cursor-pointer mb-2"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <Badge className="bg-gray-200 text-gray-600 mb-1">Legacy</Badge>
                                <h4 className="font-medium text-gray-700">{event.title}</h4>
                                {event.location && (
                                  <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {event.location}
                                  </p>
                                )}
                              </div>
                              {event.start_time && (
                                <span className="text-sm text-gray-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatTime12h(event.start_time)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            }

            const dateStr = format(day, 'yyyy-MM-dd');
            const isDragOver = dragOverDate === dateStr;

            return (
              <div
                key={idx}
                onClick={() => {
                  // Mobile: any tap on the day cell opens the Day Sheet (only when there's something to show).
                  // Desktop: keep the existing "click empty cell to create" shortcut.
                  if (isMobile || window.innerWidth < 768) {
                    const hasContent = dayJobs.length > 0 || getEventsForDate(day).length > 0;
                    if (hasContent) {
                      setDaySheetDate(day);
                      setShowDaySheet(true);
                      setV2JobsExpanded(false);
                    }
                    return;
                  }
                  if (canCreateJobs && dayJobs.length === 0) handleDateClick(day);
                }}
                onDragOver={(e) => canEditAllJobs && handleDragOver(e, day)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => canEditAllJobs && handleDrop(e, day)}
                className={`p-2 border-b border-r border-slate-200 transition-colors ${
                  !isCurrentMonth ? 'bg-slate-50' : 'bg-white'
                } ${isToday ? 'bg-amber-50' : ''} ${canCreateJobs && dayJobs.length === 0 ? 'cursor-pointer hover:bg-slate-100' : ''} ${
                  isDragOver ? 'bg-amber-100 border-amber-400 border-2' : ''
                }`}
                style={{ minHeight: `${cellHeight}px` }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium ${
                    isToday ? 'text-amber-600' : isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                  }`}>
                    {format(day, 'd')}
                  </span>
                  {canCreateJobs && isCurrentMonth && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateClick(day);
                      }}
                      className="opacity-0 group-hover:opacity-100 hover:opacity-100 w-5 h-5 flex items-center justify-center rounded bg-amber-500 text-white hover:bg-amber-600 transition-all"
                      title="Add Job"
                      data-testid={`calendar-add-btn-${format(day, 'yyyy-MM-dd')}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {/* Calendar events (PTO, Holidays, Training, etc.) — show first */}
                  {getEventsForDate(day).map(ev => {
                    const allDay = !!ev.all_day;
                    const bg = ev.color || getEventColor(ev.type);
                    // If user picked a custom color, derive readable text via contrast.
                    // Otherwise use the type's catalog text color.
                    const fg = ev.color
                      ? getContrastTextColor(ev.color)
                      : (ev.text_color || getEventTextColor(ev.type));
                    const timeLabel = !allDay && ev.start_time ? ` ${formatTime12h(ev.start_time)}` : '';
                    return (
                      <HoverCard key={`event-${ev.id}`} openDelay={300} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <div
                            draggable={isAdmin()}
                            onDragStart={(e) => handleEventDragStart(e, ev)}
                            onDragEnd={handleEventDragEnd}
                            onClick={(e) => openEditEventDialog(ev, e)}
                            className={`text-xs p-1.5 rounded cursor-pointer group relative ${
                              isAdmin() ? 'cursor-grab active:cursor-grabbing' : ''
                            } ${draggingEvent?.id === ev.id ? 'opacity-50' : ''}`}
                            style={{ backgroundColor: bg, color: fg }}
                            data-testid={`calendar-event-${ev.id}`}
                          >
                        <div className="flex items-center gap-1">
                          {/* ALL DAY pill — only for all-day events. Timed events
                              just show the start time appended to the title. */}
                          {allDay && (
                            <span
                              className="py-[3px] px-1 flex-shrink-0 inline-flex items-center justify-center text-[7px] font-bold rounded uppercase tracking-wide leading-none bg-slate-500 text-white"
                            >
                              ALL DAY
                            </span>
                          )}
                          {(ev.assigned_to || []).length > 0 && (
                            <div className="flex -space-x-1 flex-shrink-0">
                              {ev.assigned_to.slice(0, 3).map(uid => {
                                const m = getMemberInfo(uid);
                                if (!m) return null;
                                const memberBg = m.calendar_color || '#94a3b8';
                                return (
                                  <div
                                    key={uid}
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 border border-white shadow-sm"
                                    style={{ backgroundColor: memberBg, color: getContrastTextColor(memberBg) }}
                                    title={m.full_name}
                                  >
                                    {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                  </div>
                                );
                              })}
                              {ev.assigned_to.length > 3 && (
                                <div
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 border border-white shadow-sm bg-slate-600 text-white"
                                  title={`+${ev.assigned_to.length - 3} more`}
                                >
                                  +{ev.assigned_to.length - 3}
                                </div>
                              )}
                            </div>
                          )}
                          <span className="truncate flex-1 min-w-0 font-medium">{ev.title}{timeLabel}</span>
                        </div>
                      </div>
                        </HoverCardTrigger>
                        <HoverCardContent side="right" align="start" sideOffset={8} className="w-80 hidden md:block">
                          {renderEventHoverContent(ev)}
                        </HoverCardContent>
                      </HoverCard>
                    );
                  })}
                  {dayJobs.slice(0, calendarDisplayMode === 'expanded' ? 999 : (viewMode === 'week' ? 3 : 2)).map(job => (
                    <HoverCard key={job.id} openDelay={300} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <div
                          draggable={canEditThisJob(job)}
                          onDragStart={(e) => handleDragStart(e, job)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => handleJobClick(job, e)}
                          className={`text-xs p-1.5 md:p-1.5 py-2 md:py-1.5 mb-1 md:mb-0 min-h-[34px] md:min-h-0 rounded cursor-pointer group relative ${getStatusColor(job.status)} ${
                            canEditThisJob(job) ? 'cursor-grab active:cursor-grabbing' : ''
                          } ${draggingJob?.id === job.id ? 'opacity-50' : ''}`}
                          style={getStatusStyle(job.status) || undefined}
                        >
                      <div className="flex items-center gap-1">
                        {/* Daily order number */}
                        {canEditAllJobs ? (
                          editingOrder?.jobId === job.id && editingOrder?.date === format(day, 'yyyy-MM-dd') ? (
                            <input
                              type="number"
                              min="1"
                              max="99"
                              className="w-6 h-5 text-[10px] text-center border border-amber-400 rounded bg-white focus:outline-none"
                              defaultValue={editingOrder.value || ''}
                              autoFocus
                              onBlur={(e) => handleSetDailyOrder(job.id, format(day, 'yyyy-MM-dd'), e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSetDailyOrder(job.id, format(day, 'yyyy-MM-dd'), e.target.value);
                                if (e.key === 'Escape') setEditingOrder(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span
                              className={`w-5 h-5 flex-shrink-0 flex items-center justify-center text-[10px] font-bold rounded cursor-pointer ${
                                job.daily_order?.[format(day, 'yyyy-MM-dd')]
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-slate-200 text-slate-400 hover:bg-amber-100'
                              }`}
                              onClick={(e) => {
                                // On mobile, let the chip-level handler open the Day Sheet instead
                                // of opening the tiny inline-edit input which is hard to dismiss.
                                if (isMobile || window.innerWidth < 768) return;
                                e.stopPropagation();
                                setEditingOrder({
                                  jobId: job.id,
                                  date: format(day, 'yyyy-MM-dd'),
                                  value: job.daily_order?.[format(day, 'yyyy-MM-dd')] || ''
                                });
                              }}
                              title="Click to set sort order"
                            >
                              {job.daily_order?.[format(day, 'yyyy-MM-dd')] || '#'}
                            </span>
                          )
                        ) : job.daily_order?.[format(day, 'yyyy-MM-dd')] ? (
                          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-[10px] font-bold rounded bg-amber-500 text-white">
                            {job.daily_order[format(day, 'yyyy-MM-dd')]}
                          </span>
                        ) : null}
                        {/* Team member badges - show first 3, then +N */}
                        {job.assigned_team_members?.length > 0 && (
                          <div className="flex -space-x-1 flex-shrink-0">
                            {job.assigned_team_members.slice(0, 3).map(memberId => {
                              const member = getMemberInfo(memberId);
                              const bgColor = member?.calendar_color || '#f59e0b';
                              const textColor = getContrastTextColor(bgColor);
                              return (
                                <div
                                  key={memberId}
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 border border-white shadow-sm"
                                  style={{ backgroundColor: bgColor, color: textColor }}
                                  title={member?.full_name}
                                >
                                  {member?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                                </div>
                              );
                            })}
                            {job.assigned_team_members.length > 3 && (
                              <div 
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 border border-white shadow-sm bg-slate-600 text-white"
                                title={`+${job.assigned_team_members.length - 3} more`}
                              >
                                +{job.assigned_team_members.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                        <span className="truncate flex-1 min-w-0">
                          {showJobNumbers && job.job_number && (
                            <span className="font-mono text-[10px] mr-1">{job.job_number}</span>
                          )}
                          {/* Day suffix always shows for multi-day series jobs, regardless of toggle */}
                          {!showJobNumbers && job.daily_series_position && job.daily_series_total > 1 && (
                            <span className="font-mono text-[10px] mr-1 font-bold">D{job.daily_series_position}</span>
                          )}
                          {/* Strip embedded "— Day N" from title when we have an explicit prefix */}
                          {(job.daily_series_position && job.daily_series_total > 1)
                            ? (job.job_title || '').replace(/\s+—\s+Day\s+\d+\s*$/, '')
                            : job.job_title}
                        </span>
                        {canDeleteJobs && (
                          <button
                            onClick={(e) => handleDeleteClick(job, e)}
                            className="hidden md:flex opacity-0 group-hover:opacity-100 w-4 h-4 items-center justify-center rounded bg-red-500 text-white hover:bg-red-600 transition-all flex-shrink-0"
                            title="Delete"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent side="right" align="start" sideOffset={8} className="w-80 hidden md:block">
                        {renderJobHoverContent(job)}
                      </HoverCardContent>
                    </HoverCard>
                  ))}
                  {/* Legacy/Imported Events */}
                  {showLegacy && getImportedEventsForDate(day).slice(0, calendarDisplayMode === 'expanded' ? 999 : 2).map(event => (
                    <div
                      key={`legacy-${event.id}`}
                      onClick={(e) => handleLegacyEventClick(event, e)}
                      className="text-xs p-1.5 rounded cursor-pointer bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200"
                      title={`[Legacy] ${event.title} - ${event.location || 'No location'}`}
                    >
                      <div className="flex items-center gap-1">
                        <Archive className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="truncate flex-1">{event.title}</span>
                      </div>
                    </div>
                  ))}
                  {dayJobs.length > (calendarDisplayMode === 'expanded' ? 999 : (viewMode === 'week' ? 3 : 2)) && (
                    <div 
                      className="text-xs text-slate-500 pl-1 cursor-pointer hover:text-amber-600"
                      onClick={() => {
                        setSelectedDate(day);
                        setViewMode('day');
                      }}
                    >
                      +{dayJobs.length - (calendarDisplayMode === 'expanded' ? 999 : (viewMode === 'week' ? 3 : 2))} more
                    </div>
                  )}
                  {showLegacy && getImportedEventsForDate(day).length > 2 && (
                    <div 
                      className="text-xs text-gray-400 pl-1 cursor-pointer hover:text-gray-600"
                      onClick={() => {
                        setSelectedDate(day);
                        setViewMode('day');
                      }}
                    >
                      +{getImportedEventsForDate(day).length - 2} legacy
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {/* Vertical drag handle — only on resizable views. Drag to make day
            cells taller; double-click to reset to default. Persisted per
            (view-mode × display-mode) in localStorage. */}
        {isCellHeightResizable && (
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize day cells"
            data-testid="cell-height-handle"
            onMouseDown={(e) => {
              const startY = e.clientY;
              const startH = cellHeight;
              let latest = startH;
              const onMove = (ev) => {
                const dh = ev.clientY - startY;
                latest = Math.max(80, Math.min(800, startH + dh));
                setCellHeight(latest);
              };
              const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                // Persist whatever the current cell height is on release.
                try { localStorage.setItem(cellHeightKey, String(latest)); } catch { /* ignore */ }
              };
              window.addEventListener('mousemove', onMove);
              window.addEventListener('mouseup', onUp);
            }}
            onTouchStart={(e) => {
              if (!e.touches || !e.touches[0]) return;
              const startY = e.touches[0].clientY;
              const startH = cellHeight;
              let latest = startH;
              const onMove = (ev) => {
                const y = ev.touches && ev.touches[0] ? ev.touches[0].clientY : startY;
                const dh = y - startY;
                latest = Math.max(80, Math.min(800, startH + dh));
                setCellHeight(latest);
              };
              const onEnd = () => {
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend', onEnd);
                try { localStorage.setItem(cellHeightKey, String(latest)); } catch { /* ignore */ }
              };
              window.addEventListener('touchmove', onMove);
              window.addEventListener('touchend', onEnd);
            }}
            onDoubleClick={resetCellHeight}
            className="group h-3 cursor-ns-resize flex items-center justify-center bg-slate-50 hover:bg-slate-100 border-t border-slate-200 transition-colors select-none"
            title="Drag to resize • Double-click to reset"
          >
            <span className="w-10 h-1 rounded-full bg-slate-300 group-hover:bg-amber-400 transition-colors" />
          </div>
        )}
      </Card>
      </>
      )}

      {/* Job Create/Edit Dialog */}
      <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">
              {isEditMode ? (
                <span className="flex items-center gap-2 flex-wrap">
                  <span>Edit Job</span>
                  {selectedJob?.job_number && (
                    <span className="text-amber-600 font-bold">{selectedJob.job_number}</span>
                  )}
                </span>
              ) : 'Create New Job'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Quick Client Select */}
            {contacts.length > 0 && !isEditMode && (
              <div className="space-y-2">
                <Label>Quick Fill from Client</Label>
                <Select onValueChange={handleContactSelect}>
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue placeholder="Select a client to auto-fill..." />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(contact => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.company_name || contact.client_name || contact.contact_name || 'Unnamed Client'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Client Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Name *</Label>
                <Input
                  value={jobForm.client_name}
                  onChange={(e) => setJobForm(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Client or company name"
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={jobForm.phone}
                  onChange={(e) => setJobForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="bg-slate-50"
                />
              </div>
            </div>

            {/* Job Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input
                  value={jobForm.job_title}
                  onChange={(e) => setJobForm(prev => ({ ...prev, job_title: e.target.value }))}
                  placeholder="e.g., Utility Locate"
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Select 
                  value={jobForm.service_type} 
                  onValueChange={(v) => setJobForm(prev => ({ ...prev, service_type: v }))}
                >
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Service Address</Label>
                {(jobForm.service_street || jobForm.service_city) && (
                  <MapButtons 
                    address={[jobForm.service_street, jobForm.service_city, jobForm.service_state, jobForm.service_zip].filter(Boolean).join(', ')}
                    size="sm"
                    showLabels={false}
                  />
                )}
              </div>
              <Input
                value={jobForm.service_street}
                onChange={(e) => setJobForm(prev => ({ ...prev, service_street: e.target.value }))}
                placeholder="Street address"
                className="bg-slate-50"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={jobForm.service_city}
                  onChange={(e) => setJobForm(prev => ({ ...prev, service_city: e.target.value }))}
                  placeholder="City"
                  className="bg-slate-50"
                />
                <Input
                  value={jobForm.service_state}
                  onChange={(e) => setJobForm(prev => ({ ...prev, service_state: e.target.value }))}
                  placeholder="State"
                  className="bg-slate-50"
                />
                <Input
                  value={jobForm.service_zip}
                  onChange={(e) => setJobForm(prev => ({ ...prev, service_zip: e.target.value }))}
                  placeholder="ZIP"
                  className="bg-slate-50"
                />
              </div>
            </div>

            {/* Date, Time, Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={jobForm.date}
                  onChange={(e) => setJobForm(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-slate-50 w-full"
                />
                {jobForm.date && (
                  <p className="text-xs text-blue-600 font-medium">
                    Week {getWeek(new Date(jobForm.date))}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <TimeInput
                  value={jobForm.time}
                  onChange={(val) => setJobForm(prev => ({ ...prev, time: val }))}
                  className="bg-slate-50"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select 
                  value={jobForm.status} 
                  onValueChange={(v) => setJobForm(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Scheduled">Scheduled</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select 
                  value={jobForm.priority} 
                  onValueChange={(v) => setJobForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger className="bg-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Normal">Normal</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Team Assignment */}
            <div className="space-y-2">
              <Label>Assign Team Members</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
                {team.filter(m => m.active).map(member => (
                  <div
                    key={member.id}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all ${
                      jobForm.assigned_team_members.includes(member.id)
                        ? 'bg-amber-500 text-slate-900'
                        : 'bg-white border border-slate-200 hover:border-amber-500'
                    }`}
                    onClick={() => handleTeamMemberToggle(member.id)}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                      style={{ backgroundColor: member.calendar_color || '#f59e0b' }}
                    >
                      {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <span className="text-sm">{member.full_name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={jobForm.notes}
                onChange={(e) => setJobForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes or instructions..."
                rows={3}
                className="bg-slate-50"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setShowJobDialog(false)}>
              Cancel
            </Button>
            {/* Save & Open Full Editor — keeps the modal data, saves the job,
                then jumps to the full Edit Job page so the user gets the
                Day strip, photo uploads, full notes, etc. without re-typing. */}
            <Button
              variant="outline"
              onClick={() => handleSaveJob({ openFullEditor: true })}
              disabled={saving}
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              data-testid="calendar-save-open-editor-btn"
              title="Save the job and open the full editor with all fields"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save & Open Full Editor'
              )}
            </Button>
            <Button 
              onClick={() => handleSaveJob()} 
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900"
              data-testid="calendar-save-job-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditMode ? 'Update Job' : 'Create Job'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New / Edit Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-2xl">
              {editingEvent ? 'Edit Event' : 'New Event'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Create or edit a calendar event such as PTO, Holiday, Training, or Appointment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="event-title">Title *</Label>
              <Input
                id="event-title"
                value={eventForm.title}
                onChange={e => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="e.g. John PTO, Company Holiday, OSHA Training"
                data-testid="event-title-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-type">Type *</Label>
                <Select
                  value={eventForm.type}
                  onValueChange={v => setEventForm({ ...eventForm, type: v, color: '' })}
                >
                  <SelectTrigger id="event-type" data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventTypes.map(t => (
                      <SelectItem key={t.name} value={t.name}>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full inline-block"
                            style={{ backgroundColor: t.color }}
                          />
                          {t.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1 gap-3">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Switch
                    checked={eventForm.all_day}
                    onCheckedChange={v => setEventForm({ ...eventForm, all_day: v })}
                    data-testid="event-allday-switch"
                  />
                  <span className="text-sm font-medium">All-day event</span>
                </label>
                <div className="flex items-center gap-2 ml-auto">
                  <Label htmlFor="event-color" className="text-sm font-medium m-0">Color</Label>
                  <input
                    id="event-color"
                    type="color"
                    value={eventForm.color || getEventColor(eventForm.type) || '#94a3b8'}
                    onChange={e => setEventForm({ ...eventForm, color: e.target.value })}
                    className="w-10 h-8 rounded cursor-pointer border border-slate-200"
                    data-testid="event-color-picker"
                    title="Click to override default color"
                  />
                  {eventForm.color && (
                    <button
                      type="button"
                      onClick={() => setEventForm({ ...eventForm, color: '' })}
                      className="text-[10px] text-slate-500 hover:text-slate-700 underline"
                      data-testid="event-color-reset"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-date">Start Date *</Label>
                <Input
                  id="event-date"
                  type="date"
                  value={eventForm.date}
                  onChange={e => setEventForm({ ...eventForm, date: e.target.value })}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label htmlFor="event-end-date">End Date (optional)</Label>
                <Input
                  id="event-end-date"
                  type="date"
                  value={eventForm.end_date}
                  onChange={e => setEventForm({ ...eventForm, end_date: e.target.value })}
                  min={eventForm.date}
                  data-testid="event-end-date-input"
                />
              </div>
            </div>

            {!eventForm.all_day && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="event-start-time">Start Time</Label>
                  <TimeInput
                    id="event-start-time"
                    value={eventForm.start_time}
                    onChange={v => setEventForm({ ...eventForm, start_time: v })}
                    data-testid="event-start-time-input"
                  />
                </div>
                <div>
                  <Label htmlFor="event-end-time">End Time</Label>
                  <TimeInput
                    id="event-end-time"
                    value={eventForm.end_time}
                    onChange={v => setEventForm({ ...eventForm, end_time: v })}
                    data-testid="event-end-time-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label>Assigned Team Members (optional)</Label>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-1">
                {team.filter(m => m.active).map(m => {
                  const checked = eventForm.assigned_to.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => {
                          setEventForm(prev => ({
                            ...prev,
                            assigned_to: checked
                              ? prev.assigned_to.filter(id => id !== m.id)
                              : [...prev.assigned_to, m.id],
                          }));
                        }}
                        data-testid={`event-team-${m.id}`}
                      />
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                        style={{
                          backgroundColor: m.calendar_color || '#94a3b8',
                          color: getContrastTextColor(m.calendar_color || '#94a3b8'),
                        }}
                      >
                        {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                      <span className="text-sm">{m.full_name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Leave empty for company-wide events (e.g. Holidays).
              </p>
            </div>

            <div>
              <Label htmlFor="event-location">Location (optional)</Label>
              <Input
                id="event-location"
                value={eventForm.location}
                onChange={e => setEventForm({ ...eventForm, location: e.target.value })}
                placeholder="Address, venue, or meeting room — assigned team members can travel here from the time tracker"
                data-testid="event-location-input"
              />
            </div>

            <div>
              <Label htmlFor="event-notes">Notes (optional)</Label>
              <Textarea
                id="event-notes"
                value={eventForm.notes}
                onChange={e => setEventForm({ ...eventForm, notes: e.target.value })}
                rows={3}
                placeholder="Optional details..."
                data-testid="event-notes-textarea"
              />
            </div>
          </div>
          <DialogFooter className="flex-row justify-between">
            <div>
              {editingEvent && (
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleDeleteEvent}
                  data-testid="event-delete-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowEventDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveEvent}
                disabled={savingEvent}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                data-testid="event-save-btn"
              >
                {savingEvent ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {editingEvent ? 'Save Changes' : 'Create Event'}</>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Job</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedJob?.job_title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJob}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile Job Card — Rich info hybrid: status, title, client, address (tap to navigate), time, team, then actions */}
      <Dialog open={showMobileActionDialog} onOpenChange={setShowMobileActionDialog}>
        <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden">
          {selectedJob && (() => {
            const cleanTitle = (selectedJob.daily_series_position && selectedJob.daily_series_total > 1)
              ? (selectedJob.job_title || '').replace(/\s+—\s+Day\s+\d+\s*$/, '')
              : selectedJob.job_title;
            const dayBadge = selectedJob.daily_series_position && selectedJob.daily_series_total > 1
              ? `Day ${selectedJob.daily_series_position} of ${selectedJob.daily_series_total}`
              : null;
            const statusCls = getStatusColor(selectedJob.status) || 'bg-slate-100 text-slate-700';
            const fullAddress = [selectedJob.service_street, selectedJob.service_city, selectedJob.service_state, selectedJob.service_zip]
              .filter(Boolean).join(', ') || selectedJob.service_address || '';

            return (
              <>
                {/* Header: status + priority. Built-in shadcn Dialog X close lives at top-right automatically. */}
                <div className="flex items-start gap-2 px-4 pt-4 pb-3 pr-12 border-b border-slate-100">
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                    {selectedJob.status && (
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide ${statusCls}`}
                        style={getStatusStyle(selectedJob.status) || undefined}
                      >
                        {selectedJob.status}
                      </span>
                    )}
                    {selectedJob.priority && selectedJob.priority !== 'Normal' && (
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide ${getPriorityColor ? getPriorityColor(selectedJob.priority) : 'bg-amber-100 text-amber-700'}`}>
                        {selectedJob.priority}
                      </span>
                    )}
                    {dayBadge && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-medium">
                        {dayBadge}
                      </span>
                    )}
                  </div>
                </div>

                {/* Body: title + meta */}
                <div className="px-4 py-3 space-y-2.5">
                  <h2 className="text-lg font-bold text-slate-900 leading-snug font-['Barlow_Condensed']">
                    {cleanTitle || 'Untitled Job'}
                  </h2>

                  {selectedJob.client_name && (
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{selectedJob.client_name}</span>
                    </div>
                  )}

                  {fullAddress && (
                    <div className="space-y-1.5" data-testid="mobile-card-address">
                      <div className="flex items-start gap-2 text-sm text-slate-700 leading-snug">
                        <MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                        <span className="flex-1">{fullAddress}</span>
                      </div>
                      <MapButtons address={fullAddress} className="ml-6" />
                    </div>
                  )}

                  {selectedJob.time && (
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span>{selectedJob.time}{selectedJob.duration ? ` · ${selectedJob.duration}h` : ''}</span>
                    </div>
                  )}

                  {selectedJob.assigned_team_members?.length > 0 && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Team</span>
                      <div className="flex -space-x-1">
                        {selectedJob.assigned_team_members.slice(0, 6).map(memberId => {
                          const m = getMemberInfo(memberId);
                          if (!m) return null;
                          const bg = m.calendar_color || '#94a3b8';
                          return (
                            <div
                              key={memberId}
                              className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm"
                              style={{ backgroundColor: bg, color: getContrastTextColor(bg) }}
                              title={m.full_name}
                            >
                              {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                          );
                        })}
                        {selectedJob.assigned_team_members.length > 6 && (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm bg-slate-600 text-white">
                            +{selectedJob.assigned_team_members.length - 6}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedJob.notes && (
                    <p className="text-xs text-slate-500 line-clamp-2 leading-snug border-t border-slate-100 pt-2 mt-2">
                      {selectedJob.notes}
                    </p>
                  )}
                </div>

                {/* Action buttons */}
                <div className="border-t border-slate-100 bg-slate-50 px-3 py-3 space-y-2">
                  <Button
                    className="w-full justify-center h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                    onClick={() => {
                      setShowMobileActionDialog(false);
                      navigate(`/jobs/${selectedJob?.id}`, { state: { from: 'calendar' } });
                    }}
                    data-testid="mobile-action-view"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    View Full Details
                  </Button>
                  <div className="grid grid-cols-2 gap-2">
                    {canEditThisJob(selectedJob) && (
                      <Button
                        variant="outline"
                        className="h-11 justify-center"
                        onClick={() => {
                          setShowMobileActionDialog(false);
                          openJobForEdit(selectedJob);
                        }}
                        data-testid="mobile-action-edit"
                      >
                        <Edit className="w-4 h-4 mr-2 text-blue-600" />
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="h-11 justify-center"
                      onClick={() => {
                        navigate('/field-tracker-v2');
                        setShowMobileActionDialog(false);
                      }}
                      data-testid="mobile-action-time-tracker"
                    >
                      <Timer className="w-4 h-4 mr-2 text-purple-600" />
                      Time Tracker
                    </Button>
                  </div>
                  {canDeleteJobs && (
                    <Button
                      variant="outline"
                      className="w-full h-10 justify-center border-red-200 hover:bg-red-50 text-red-600"
                      onClick={() => {
                        setShowMobileActionDialog(false);
                        setShowDeleteDialog(true);
                      }}
                      data-testid="mobile-action-delete"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Job
                    </Button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Mobile Day Sheet — list of jobs/events for tapped day */}
      <Dialog open={showDaySheet} onOpenChange={setShowDaySheet}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden max-h-[85vh] flex flex-col">
          {daySheetDate && (() => {
            const sheetJobs = getJobsForDate(daySheetDate);
            const sheetEvents = getEventsForDate(daySheetDate);
            return (
              <>
                <div className="flex items-center px-4 py-3 pr-12 border-b border-slate-100 flex-shrink-0">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">{format(daySheetDate, 'EEEE')}</p>
                    <h2 className="text-lg font-bold text-slate-900 font-['Barlow_Condensed']">
                      {format(daySheetDate, 'MMMM d, yyyy')}
                    </h2>
                  </div>
                </div>

                <div className="overflow-y-auto flex-1 px-3 py-3 space-y-2">
                  {/* Events first */}
                  {sheetEvents.map(ev => {
                    const bg = ev.color || getEventColor(ev.type);
                    const fg = ev.color ? getContrastTextColor(ev.color) : (ev.text_color || getEventTextColor(ev.type));
                    const timeLabel = !ev.all_day && ev.start_time ? formatTime12h(ev.start_time) : (ev.all_day ? 'All day' : '');
                    return (
                      <button
                        key={`event-${ev.id}`}
                        onClick={() => {
                          setShowDaySheet(false);
                          setEditingEvent(ev);
                          setShowEventDialog(true);
                        }}
                        className="w-full text-left rounded-lg p-3 flex items-start gap-3 transition-shadow active:scale-[0.99]"
                        style={{ backgroundColor: bg, color: fg }}
                        data-testid={`day-sheet-event-${ev.id}`}
                      >
                        <Calendar className="w-5 h-5 flex-shrink-0 mt-0.5 opacity-80" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-bold uppercase tracking-wider opacity-80">{ev.type || 'Event'}</span>
                            {timeLabel && <span className="text-[11px] font-semibold opacity-90">{timeLabel}</span>}
                          </div>
                          <p className="font-semibold leading-snug mt-0.5">{ev.title}</p>
                          {ev.notes && <p className="text-xs opacity-80 mt-1 line-clamp-2">{ev.notes}</p>}
                        </div>
                      </button>
                    );
                  })}

                  {/* Jobs (V2: roll-up summary tile until expanded) */}
                  {isV2Calendar && sheetJobs.length > 0 && !v2JobsExpanded && (
                    <button
                      type="button"
                      onClick={() => setV2JobsExpanded(true)}
                      className="w-full text-left rounded-lg p-4 bg-amber-500 text-slate-900 shadow-sm flex items-center justify-between active:scale-[0.99] transition"
                      data-testid="day-sheet-jobs-rollup"
                    >
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-wider opacity-80">Jobs</p>
                        <p className="text-lg font-bold leading-tight">
                          {sheetJobs.length} {sheetJobs.length === 1 ? 'Job' : 'Jobs'} Scheduled
                        </p>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">View All →</span>
                    </button>
                  )}
                  {(!isV2Calendar || v2JobsExpanded) && sheetJobs.map(job => {
                    const statusCls = getStatusColor(job.status) || 'bg-slate-100 text-slate-700';
                    const cleanTitle = (job.daily_series_position && job.daily_series_total > 1)
                      ? (job.job_title || '').replace(/\s+—\s+Day\s+\d+\s*$/, '')
                      : job.job_title;
                    const order = job.daily_order?.[format(daySheetDate, 'yyyy-MM-dd')];
                    return (
                      <button
                        key={job.id}
                        onClick={() => {
                          setSelectedJob(job);
                          setShowDaySheet(false);
                          // Slight delay so the close animation doesn't fight the open
                          setTimeout(() => setShowMobileActionDialog(true), 80);
                        }}
                        className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3 hover:border-amber-300 hover:bg-amber-50/30 transition-colors active:scale-[0.99]"
                        data-testid={`day-sheet-job-${job.id}`}
                      >
                        <div
                          className={`w-9 h-9 rounded-md flex items-center justify-center font-bold text-sm flex-shrink-0 ${statusCls}`}
                          style={getStatusStyle(job.status) || undefined}
                        >
                          {order || (job.daily_series_position && job.daily_series_total > 1 ? `D${job.daily_series_position}` : '#')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            {job.status && (
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide ${statusCls}`}
                                style={getStatusStyle(job.status) || undefined}
                              >
                                {job.status}
                              </span>
                            )}
                            {job.priority === 'Urgent' && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wide bg-red-100 text-red-700">
                                URGENT
                              </span>
                            )}
                            {job.time && <span className="text-[10px] text-slate-500 font-medium">{job.time}</span>}
                          </div>
                          <p className="font-semibold text-slate-900 leading-snug truncate">{cleanTitle || 'Untitled Job'}</p>
                          {job.client_name && (
                            <p className="text-xs text-slate-500 truncate">{job.client_name}</p>
                          )}
                          {job.assigned_team_members?.length > 0 && (
                            <div className="flex -space-x-1 mt-1.5">
                              {job.assigned_team_members.slice(0, 5).map(mid => {
                                const m = getMemberInfo(mid);
                                if (!m) return null;
                                const bg = m.calendar_color || '#94a3b8';
                                return (
                                  <div
                                    key={mid}
                                    className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold border border-white shadow-sm"
                                    style={{ backgroundColor: bg, color: getContrastTextColor(bg) }}
                                    title={m.full_name}
                                  >
                                    {m.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                  </div>
                                );
                              })}
                              {job.assigned_team_members.length > 5 && (
                                <div className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold border border-white shadow-sm bg-slate-600 text-white">
                                  +{job.assigned_team_members.length - 5}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}

                  {sheetJobs.length === 0 && sheetEvents.length === 0 && (
                    <div className="py-12 text-center text-slate-400 text-sm">
                      Nothing scheduled for this day
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Import ICS Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-gray-600" />
              Import Calendar Events
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Import events from an ICS file (e.g., exported from TeamUp, Google Calendar, or Outlook).
              Imported events will appear as "Legacy Jobs" on the calendar.
            </p>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-3">
                Select an .ics file to import
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".ics"
                  onChange={handleImportICS}
                  className="hidden"
                  disabled={importing}
                  data-testid="ics-file-input"
                />
                <Button
                  variant="outline"
                  className="pointer-events-none"
                  disabled={importing}
                >
                  {importing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    'Choose File'
                  )}
                </Button>
              </label>
            </div>

            {importStats?.total_events > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700">Current Imports</p>
                <p className="text-slate-600">
                  {importStats.total_events} events imported
                  {importStats.oldest_event && importStats.newest_event && (
                    <span className="text-slate-500">
                      {' '}({importStats.oldest_event} to {importStats.newest_event})
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Legacy Event Detail Dialog */}
      <Dialog open={showLegacyDetailDialog} onOpenChange={setShowLegacyDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-gray-500" />
              Legacy Job Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedLegacyEvent && (
            <div className="py-4 space-y-4">
              <div>
                <Badge className="bg-gray-200 text-gray-600 mb-2">Legacy / Imported</Badge>
                <h3 className="text-lg font-semibold text-slate-900">{selectedLegacyEvent.title}</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-700">Date & Time</p>
                    <p className="text-sm text-slate-600">
                      {selectedLegacyEvent.start_date}
                      {selectedLegacyEvent.start_time && ` at ${formatTime12h(selectedLegacyEvent.start_time)}`}
                      {selectedLegacyEvent.all_day && ' (All day)'}
                    </p>
                  </div>
                </div>

                {selectedLegacyEvent.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Location</p>
                      <p className="text-sm text-slate-600">{selectedLegacyEvent.location}</p>
                      <MapButtons 
                        address={selectedLegacyEvent.location}
                        size="sm"
                        showLabels={false}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {selectedLegacyEvent.description && (
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">Description</p>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedLegacyEvent.description}</p>
                    </div>
                  </div>
                )}

                <div className="pt-3 border-t border-slate-200 text-xs text-slate-400">
                  <p>Source: {selectedLegacyEvent.source || 'ICS Import'}</p>
                  <p>Imported by: {selectedLegacyEvent.imported_by_name}</p>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLegacyDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
